<template>
  <div>
    <v-expansion-panels color="#B4D8D7" variant="accordion">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-row class="analysisTitleRow">
            <v-col cols="8">
              Income
            </v-col>
            <v-col class="right">
              {{ formatter.format(Math.round(selectedIncomeData.totalIn), false, false) }}
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SpendingAnalysisSubCategory 
            v-for="group in selectedIncomeData.groupedAnalysis"
            :key="group" 
            :group="group"
            :income="true"
            :planPage="false"
            :bgColour="`#B4D8D7`" 
            :formatter="formatter"
            @categoryClicked="(c) => $emit('categoryClicked', c, 0)"
            @uncatagorisedTransactionsClicked="(c) => $emit('uncatagorisedTransactionsClicked', c)">
          </SpendingAnalysisSubCategory>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-expansion-panels color="#EFE8E6" class="mt-4" variant="accordion">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-row class="analysisTitleRow">
            <v-col cols="8">
              Future plans
            </v-col>
            <v-col class="right">
              {{ formatter.format(Math.abs(Math.round(selectedFuturePlansData.totalNet)), false, false) }}
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SpendingAnalysisSubCategory 
            v-for="group in selectedFuturePlansData.groupedAnalysis"
            :key="group" 
            :group="group"
            :plans="spendingPlans"
            :planPage="false"
            bgColour="#EFE8E6" 
            :isCollapsed="isFuturePlansCollapsed"
            :formatter="formatter"
            @categoryClicked="(c) => $emit('categoryClicked', c, 1)"
            @uncatagorisedTransactionsClicked="(c) => $emit('uncatagorisedTransactionsClicked', c)">
          </SpendingAnalysisSubCategory>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel color="#EEF0FF">
        <v-expansion-panel-title>
          <v-row class="analysisTitleRow">
            <v-col cols="8">
              Everyday spending
            </v-col>
            <v-col class="right">
              {{ formatter.format(Math.abs(Math.round(selectedEverydaySpendData.totalNet)), false, false) }}
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SpendingAnalysisSubCategory 
            v-for="group in selectedEverydaySpendData.groupedAnalysis"  
            :key="group" 
            :group="group"
            :plans="spendingPlans"
            :planPage="false"
            bgColour="#EEF0FF" 
            :isCollapsed="isEveryDaySpendCollapsed"
            :formatter="formatter"
            @categoryClicked="(c) => $emit('categoryClicked', c, 2)"
            @uncatagorisedTransactionsClicked="(c) => $emit('uncatagorisedTransactionsClicked', c)">
          </SpendingAnalysisSubCategory>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel color="#413655">
        <v-expansion-panel-title>
          <v-row class="analysisTitleRow">
            <v-col cols="8">
              Committed costs
            </v-col>
            <v-col class="right">
              {{ formatter.format(Math.abs(Math.round(selectedCommittedCostsData.totalNet)), false, false) }}
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SpendingAnalysisSubCategory 
            v-for="group in selectedCommittedCostsData.groupedAnalysis"
            :key="group" 
            :group="group"
            :plans="spendingPlans"
            :planPage="false"
            :bgColour="`#413655`"
            :isCollapsed="isCommittedCostsCollapsed"
            :formatter="formatter"
            @categoryClicked="(c) => $emit('categoryClicked', c, 3)"
            @uncatagorisedTransactionsClicked="(c) => $emit('uncatagorisedTransactionsClicked', c)">
          </SpendingAnalysisSubCategory>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import SpendingAnalysisSubCategory from './SpendingAnalysisSubCategory.vue';
import { GRANDPARENT_CLASSIFICATIONS } from '../../mixins/SpendingAnalysis';

export default {
  name: 'SpendingCategoryPanels',
  components: {
    SpendingAnalysisSubCategory
  },
  props: {
    spendingAnalysisStructured: {
      type: Object,
      required: true
    },
    selectedSlotKey: {
      type: String,
      required: true
    },
    spendingPlans: {
      type: Object,
      required: true
    },
    formatter: {
      type: Object,
      required: true
    },
    isFuturePlansCollapsed: {
      type: Boolean,
      default: true
    },
    isEveryDaySpendCollapsed: {
      type: Boolean,
      default: true
    },
    isCommittedCostsCollapsed: {
      type: Boolean,
      default: true
    }
  },
  emits: ['categoryClicked', 'uncatagorisedTransactionsClicked'],
  data() {
    return {
      allIncome: [],
      allEveryDaySpend: [],
      allFuturePlans: [],
      allCommittedCosts: [],
      selectedIncomeData: { totalIn: 0, groupedAnalysis: [] },
      selectedEverydaySpendData: { totalNet: 0, groupedAnalysis: [] },
      selectedFuturePlansData: { totalNet: 0, groupedAnalysis: [] },
      selectedCommittedCostsData: { totalNet: 0, groupedAnalysis: [] }
    };
  },
  watch: {
    selectedSlotKey() {
      this.getStructuredCategories();
      this.updateSelectedCategoryData();
    },
    spendingAnalysisStructured: {
      handler() {
        this.getStructuredCategories();
        this.updateSelectedCategoryData();
      },
      immediate: true
    }
  },
  mounted() {
    this.getStructuredCategories();
    this.updateSelectedCategoryData();
  },
  methods: {
    updateSelectedCategoryData() {
      this.selectedIncomeData = this.getSelectedIncome();
      this.selectedEverydaySpendData = this.getSelectedEverydaySpend();
      this.selectedFuturePlansData = this.getSelectedFuturePlans();
      this.selectedCommittedCostsData = this.getSelectedCommittedCosts();
    },

    getSelectedIncome() {
      var selected = this.allIncome.find(s => s.slotName === this.selectedSlotKey);
      if (!selected) {
        selected = {
          label: this.selectedSlotKey,
          totalIn: 0,
          totalOut: 0,
          totalNet: 0,
          groupedAnalysis: []
        };
      }
      return selected;
    },

    getSelectedEverydaySpend() {
      return this.selectedCategory(this.allEveryDaySpend, GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND);
    },

    getSelectedFuturePlans() {
      return this.selectedCategory(this.allFuturePlans, GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING);
    },

    getSelectedCommittedCosts() {
      return this.selectedCategory(this.allCommittedCosts, GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS);
    },

    selectedCategory(categoryArray, grandparentClassification) {
      var selected = categoryArray.find(s => s.slotName === this.selectedSlotKey);
      if (!selected) {
        selected = {
          label: this.selectedSlotKey,
          totalIn: 0,
          totalOut: 0,
          totalNet: 0,
          groupedAnalysis: []
        };
      }
      // add any missing groups that we have plans for
      var newGroups = [];
      var plans = this.spendingPlans.plans.filter(plan => plan.classification.grandParentClassification === grandparentClassification);
      plans.forEach(plan => {
        // if this plan isn't found in the analysis, add it
        if (!selected.groupedAnalysis.find(g => g.name === plan.classification.fullName)) {
          newGroups.push({
            name: plan.classification.fullName,
            transactionClassification: plan.classification,
            amountIn: 0,
            amountOut: 0,
            amountNet: 0
          });
        }
      });
      selected.groupedAnalysis = selected.groupedAnalysis.concat(newGroups);
      // sort the groups by amountOut (abs)
      selected.groupedAnalysis.sort((a, b) => Math.abs(b.amountNet) - Math.abs(a.amountNet));
      // If the groupedAnalysis.name is 'Uncategorised' then change then name to: 'Tag n transactions'.  Get n from groupedAnalysis.countNegativeTransactions
      selected.groupedAnalysis.forEach(group => {
        if (group.name === 'Uncategorized') {
          group.name = 'Tag ' + group.countNegativeTransactions + ' transaction' + (group.countNegativeTransactions !== 1 ? 's' : '');
        }
      });
      // Move the 'Tag n transactions' group to the top
      const uncategorizedGroupIndex = selected.groupedAnalysis.findIndex(group => group.name.startsWith('Tag '));
      if (uncategorizedGroupIndex > -1) {
        const [uncategorizedGroup] = selected.groupedAnalysis.splice(uncategorizedGroupIndex, 1);
        selected.groupedAnalysis.unshift(uncategorizedGroup);
      }
      return selected;
    },
    getStructuredCategories() {
        this.allEveryDaySpend = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND);
        this.allCommittedCosts = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS);
        this.allFuturePlans = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING);
        this.allIncome = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.INCOME);
        this.slots = this.spendingAnalysisStructured.slotNames;
    },
  }
};
</script>

<style scoped>
.analysisTitleRow .v-col {
  font-size: 15px !important;
}
</style>
